/**
 * The presenter photo library (F-134), as the client reads it: a module
 * SINGLETON loaded from `GET /api/presenter-photos` when the app opens, when
 * a board opens, and whenever an admin edits it — the same shape and the
 * same reasons as the AI model list (features/ai-models/store.ts).
 *
 * The thumbnail panel reads it to say "the presenter comes from the library"
 * and to show which photo each frame was built on; the admin page reads it
 * to manage it. Neither fetches it — the fetch lives in ./load.ts, so a
 * component in the board's import graph never drags `core/api.ts` (and its
 * Vite-only `import.meta.env`) into the server's e2e typecheck.
 */
import { isPresenterPhotoOffered, type PresenterPhoto } from "@kola/shared";
import { useMemo } from "react";
import { useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";

export interface PresenterPhotosState {
  /** Every photo, library order (oldest first). */
  photos: readonly PresenterPhoto[];
  /** True once the first load (or a later refresh) has answered. */
  loaded: boolean;
  setPhotos(photos: readonly PresenterPhoto[]): void;
}

export type PresenterPhotosStore = StoreApi<PresenterPhotosState>;

export const presenterPhotos: PresenterPhotosStore = createStore<PresenterPhotosState>((set) => ({
  photos: [],
  loaded: false,
  setPhotos: (photos) => set({ photos: [...photos], loaded: true }),
}));

/** The whole library, live — the admin page and the gallery's "which photo" cue. */
export function usePresenterPhotos(): readonly PresenterPhoto[] {
  return useStore(presenterPhotos, (s) => s.photos);
}

export function usePresenterPhotosLoaded(): boolean {
  return useStore(presenterPhotos, (s) => s.loaded);
}

/**
 * The photos a run will actually be offered — tagged and not vetoed. What
 * the thumbnail panel counts when it says the presenter comes from the
 * library. Memoised: a selector that builds a fresh array spins
 * `useSyncExternalStore` (see the model list store).
 */
export function useOfferedPresenterPhotos(): readonly PresenterPhoto[] {
  const photos = usePresenterPhotos();
  return useMemo(() => photos.filter(isPresenterPhotoOffered), [photos]);
}
